import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Nav from '../components/Nav';
import Footer from '../components/Footer';
import { supabase } from '../lib/supabase';
import type { Offer, Vendor } from '../lib/database.types';
import { formatMoney } from '../lib/format';

type Card = Vendor & { offer: Offer | null };

export default function LocationsList() {
  const [bars, setBars] = useState<Card[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('All');

  useEffect(() => {
    (async () => {
      const { data: vendors } = await supabase
        .from('ga_vendors')
        .select('*')
        .eq('status', 'active')
        .order('name');

      if (vendors && vendors.length > 0) {
        const { data: offers } = await supabase
          .from('ga_offers')
          .select('*')
          .in('vendor_id', vendors.map((v: Vendor) => v.id))
          .eq('status', 'active');
        const byVendor = new Map<string, Offer>();
        (offers as Offer[] | null)?.forEach((o) => {
          if (!byVendor.has(o.vendor_id)) byVendor.set(o.vendor_id, o);
        });
        setBars((vendors as Vendor[]).map((v) => ({ ...v, offer: byVendor.get(v.id) ?? null })));
      } else {
        setBars([]);
      }
      setLoading(false);
    })();
  }, []);

  const neighborhoods = ['All', ...new Set(bars.map((b) => b.neighborhood).filter(Boolean) as string[])];
  const filtered = filter === 'All' ? bars : bars.filter((b) => b.neighborhood === filter);

  return (
    <>
      <Nav />
      <section style={{ paddingTop: 56, paddingBottom: 48 }}>
        <div className="container">
          <div className="eyebrow" style={{ marginBottom: 22 }}>Locations · {bars.length} live</div>
          <h1 className="display" style={{ fontSize: 'clamp(48px, 7vw, 96px)', margin: 0 }}>
            Find your <span className="serif-italic" style={{ color: 'var(--wine)' }}>spot.</span>
          </h1>
        </div>
      </section>

      {bars.length === 0 && !loading && (
        <section style={{ paddingBottom: 32 }}>
          <div className="container">
            <div style={{ padding: 32, borderRadius: 18, background: 'var(--card)', maxWidth: 720 }}>
              <div className="display" style={{ fontSize: 26, marginBottom: 8 }}>No locations live yet.</div>
              <p className="muted" style={{ marginTop: 0 }}>
                We're onboarding bars across Manhattan. Want to suggest a spot? <Link to="/request-location" style={{ textDecoration: 'underline' }}>Browse our short list →</Link>
              </p>
            </div>
          </div>
        </section>
      )}

      {bars.length > 0 && (
        <section style={{ paddingBottom: 32 }}>
          <div className="container">
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {neighborhoods.map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className="mono"
                  style={{
                    padding: '10px 16px',
                    borderRadius: 999,
                    border: '1px solid var(--ink)',
                    background: filter === f ? 'var(--ink)' : 'transparent',
                    color: filter === f ? 'var(--bg)' : 'var(--ink)',
                    fontSize: 12,
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    cursor: 'pointer',
                  }}
                >{f}</button>
              ))}
            </div>
          </div>
        </section>
      )}

      <section style={{ paddingBottom: 120 }}>
        <div className="container">
          {loading ? (
            <div style={{ padding: 80, textAlign: 'center', color: 'var(--ink-soft)' }}>Loading…</div>
          ) : (
            <div className="r-grid-3">
              {filtered.map((b) => <Card key={b.id} bar={b} />)}
            </div>
          )}
        </div>
      </section>
      <Footer />
    </>
  );
}

function Card({ bar }: { bar: Card }) {
  return (
    <Link to={`/locations/${bar.slug}`} className="bar-card" style={{ display: 'block' }}>
      <div
        style={{
          width: '100%',
          aspectRatio: '4 / 3',
          borderRadius: 16,
          overflow: 'hidden',
          background: bar.hero_image_url
            ? `url(${bar.hero_image_url}) center/cover no-repeat`
            : `linear-gradient(135deg, oklch(0.72 0.12 ${(bar.name.charCodeAt(0) * 7) % 360}), oklch(0.38 0.13 ${((bar.name.charCodeAt(0) * 7) + 30) % 360}))`,
        }}
      />
      <div style={{ paddingTop: 16 }}>
        <div className="row-between">
          <h3 className="display" style={{ fontSize: 24, margin: 0 }}>{bar.name}</h3>
          <span className="mono" style={{ fontSize: 11, letterSpacing: '0.12em', color: 'var(--ink-soft)', textTransform: 'uppercase' }}>{bar.neighborhood}</span>
        </div>
        <div style={{ fontSize: 13, color: 'var(--ink-soft)', marginTop: 4, marginBottom: 12 }}>
          {bar.address} · {bar.vibes.slice(0, 2).join(' · ')}
        </div>
        {bar.offer && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 12, borderTop: '1px solid var(--line)' }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: 15 }}>{bar.offer.title}</div>
              <div style={{ fontSize: 12, color: 'var(--ink-soft)', marginTop: 2 }}>
                {formatMoney(bar.offer.price_cents)}/head · {bar.offer.window_days}
              </div>
            </div>
            <span className="display" style={{ fontSize: 22, color: 'var(--primary)' }}>→</span>
          </div>
        )}
      </div>
    </Link>
  );
}
